import { captureMessage } from '@sentry/react';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { useProfile } from 'init/use-enforce-profile';
import type { CompanyConfiguration } from 'shared/hooks/use-load-company-configuration';
import type { ApplicationLocale } from 'types';
import { AVAILABLE_CURRENCIES, type CurrencyCode, type CurrencySymbol } from 'types/currency';
import type { Debtor } from 'types/store/client-state';

/**
 * @deprecated The currency should only:
 * - be part of a localizedAmount object
 * - be assumed to be EUR in other cases
 */
export function currencySymbol(
  currencySuffix?: CurrencyCode | undefined,
  fromCurrencyItem: CompanyConfiguration | Debtor | null = null,
) {
  const currency = currencySuffix ?? fromCurrencyItem?.currency ?? 'EUR';

  return AVAILABLE_CURRENCIES.find(c => c.code === currency)?.symbol ?? '€';
}

/**
 * @deprecated Use `useLocalizedCurrencyFormatter` instead
 */
export const formatAmount = (
  value: any,
  _decimal = ',',
  _thousands = '.',
  _decimalCount = 2,
  currencySymbolStr: CurrencySymbol = '€',
  locale: ApplicationLocale = 'fr',
) => {
  const options: Intl.NumberFormatOptions = {};

  const currencyFromSymbol =
    AVAILABLE_CURRENCIES.find(c => c.symbol === currencySymbolStr)?.code ?? 'EUR';

  options.style = 'currency';
  options.currency = currencyFromSymbol;

  const formatter = Intl.NumberFormat(locale, options);

  return formatter.format(value);
};

// See https://recovr.sentry.io/issues/4697076958/
// The problem is most likely a compatibility issue with the more recent `notation: 'compact'` option
function supportsCompactNotation() {
  try {
    new Intl.NumberFormat('en', { notation: 'compact' }).format(1000);
    return true;
  } catch (_e) {
    captureMessage(
      'Intl.NumberFormat does not support compact notation, falling back to long notation',
    );
    return false;
  }
}

/**
 * Creates a localized currency formatter
 * @param stripCurrency Whether to strip the currency symbol
 * @param currency Optional currency code to use
 * @param additionalOptions Additional options to pass to Intl.NumberFormat
 * @returns Intl.NumberFormat instance
 */
export function useLocalizedCurrencyFormatter(
  stripCurrency: boolean = false,
  currency?: CurrencyCode,
  additionalOptions: Intl.NumberFormatOptions = {},
) {
  const profile = useProfile();
  const company = useCompanyConfiguration();

  const { locale } = profile;
  const options = {
    trailingZeroDisplay: 'stripIfInteger' as const,
    ...additionalOptions,
  };

  if (!stripCurrency) {
    options.style = 'currency';
    options.currency = currency ?? company?.currency ?? 'EUR';
  }

  return new Intl.NumberFormat(locale, options);
}

/**
 * Formatter that uses the compact notation if available without breaking on older browsers.
 * If compact is not available, the long format will be used.
 * @param stripCurrency Whether to strip the currency symbol
 * @param currency Optional currency code to use
 * @returns Intl.NumberFormat instance
 */
export function useSafeLocalizedCompactCurrencyFormatter(
  stripCurrency: boolean = false,
  currency?: CurrencyCode,
) {
  const compactOptions: Intl.NumberFormatOptions = {};

  if (supportsCompactNotation()) compactOptions.notation = 'compact';

  return useLocalizedCurrencyFormatter(stripCurrency, currency, compactOptions);
}

export function getCurrencySymbolFromCurrencyCode(currencyCode: CurrencyCode) {
  return AVAILABLE_CURRENCIES.find(c => c.code === currencyCode)?.symbol ?? '€';
}
